import {
  type Configuration,
  type RedirectRequest,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { SeverityLevel, trackTrace } from '../lib/telemetry';

const tenantSubdomain = import.meta.env.VITE_ENTRA_TENANT_SUBDOMAIN;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;

const authority = `https://${tenantSubdomain}.ciamlogin.com/`;

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    knownAuthorities: [`${tenantSubdomain}.ciamlogin.com`],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      // MSAL logger callback. `LogLevel.Error` is forwarded to App Insights as
      // a trace (`SeverityLevel.Error`) so MSAL-internal failures (token
      // acquisition, network, configuration) surface in the same telemetry
      // backend as application exceptions. In `development` mode, the message
      // is also echoed to `console.error` for DevTools visibility.
      //
      // PII discipline: `piiLoggingEnabled: false` (below) instructs MSAL to
      // scrub PII before invoking this callback. The `message` parameter at
      // `LogLevel.Error` therefore should not carry user identifiers, but the
      // accept-path is treated as best-effort — never elevate this logger to
      // `LogLevel.Verbose` without also forcing `piiLoggingEnabled: false`
      // and re-auditing forwarded payloads.
      loggerCallback: (level: LogLevel, message: string) => {
        if (level === LogLevel.Error) {
          trackTrace(message, 'msal', SeverityLevel.Error);
          if (import.meta.env.MODE === 'development') {
            console.error('[MSAL]', message);
          }
        }
      },
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest: RedirectRequest = {
  scopes: [import.meta.env.VITE_ENTRA_API_SCOPE],
};

export const tokenRequest = {
  scopes: [import.meta.env.VITE_ENTRA_API_SCOPE],
};
